import { Grid, Typography, useTheme, IconButton, Rating, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Tooltip } from '@mui/material';
import { ScreenComponent } from '../models/enums/ScreenComponents';
import { Product } from '../models/DB';
import { Image, View } from 'react-native';
import { getBadgeText } from '../utils/customFunctions';
import { memo, useState } from 'react';
import { ProductPrice } from './ProductPrice';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { cartStore } from '../app/stores/Cart';
import { useCachedImage } from '../app/hooks/custom/useCachedImage';
import { usePreviewableConfig } from '../contexts/PreviewConfigContext';
import type { GridDensity } from './ProductsLayout';

interface ProductItemProps {
    /** Product data - required unless using override props in preview mode */
    product?: Product;
    /** Navigation object - required for interactive mode */
    navigation?: any;
    /** Dark mode flag */
    darkMode?: boolean;
    /** Preview mode - disables all interactivity (navigation, cart, dialogs) */
    previewMode?: boolean;
    /** Fixed width for preview contexts */
    width?: number | string;
    // Override props for preview mode (useful for unsaved changes)
    /** Override product name */
    nameOverride?: string;
    /** Override price */
    priceOverride?: number;
    /** Override discount */
    discountOverride?: number;
    /** Override discount type */
    discountTypeOverride?: 'percentage' | 'value';
    /** Override thumbnail URL (for new/unsaved images) */
    thumbnailUrlOverride?: string;
    /** Override thumbnail resize mode */
    thumbnailResizeModeOverride?: 'cover' | 'contain' | 'center' | 'stretch' | 'repeat';
    /** Override inStock status */
    inStockOverride?: boolean;
    /** Override showRating */
    showRatingOverride?: boolean;
    /** Override font family */
    fontFamilyOverride?: string;
    /** Grid density for responsive column sizing */
    gridDensity?: GridDensity;
}

export const ProductItem = memo(({
    product,
    navigation,
    darkMode = false,
    previewMode = false,
    width,
    nameOverride,
    priceOverride,
    discountOverride,
    discountTypeOverride,
    thumbnailUrlOverride,
    thumbnailResizeModeOverride,
    inStockOverride,
    showRatingOverride,
    fontFamilyOverride,
    gridDensity = 'standard'
}: ProductItemProps) => {
    const theme = useTheme();
    const { config } = usePreviewableConfig();
    const { addToCart } = cartStore((state) => state);
    const [showVariantDialog, setShowVariantDialog] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState<string | undefined>(undefined);

    // Resolve values - overrides take precedence over product values
    const name = nameOverride ?? product?.name ?? 'Product Name';
    const price = priceOverride ?? product?.price ?? 0;
    const discount = discountOverride ?? product?.discount ?? 0;
    const discountType = discountTypeOverride ?? product?.discountType ?? 'percentage';
    const inStock = inStockOverride ?? product?.inStock ?? true;
    const showRating = showRatingOverride ?? product?.showRating ?? true;
    
    // For thumbnail, use override first, then product image
    const productThumbnailUrl = product?.images?.['thumbnail']?.url;
    const thumbnailUrl = thumbnailUrlOverride ?? productThumbnailUrl;
    const thumbnailResizeMode = thumbnailResizeModeOverride 
        ?? product?.images?.['thumbnail']?.resizeMode 
        ?? product?.thumbnailResizeMode 
        ?? 'cover';

    // Use cached image hook only for non-override URLs
    const { imageSource: cachedImageSource } = useCachedImage(
        !thumbnailUrlOverride ? productThumbnailUrl : undefined
    );
    
    // Determine final image source
    const imageSource = thumbnailUrlOverride 
        ? { uri: thumbnailUrlOverride } 
        : cachedImageSource;
    
    // Use Google Font if set, otherwise use local font
    const primaryFontFamily = fontFamilyOverride 
        ?? (config.primaryFont ? `'${config.primaryFont}', sans-serif` : 'primaryFont');

    const handleProductPress = () => {
        if (previewMode || !navigation) return;
        navigation.navigate(ScreenComponent.Product, { id: product?.id });
    };

    const handleCartClick = (e: React.MouseEvent) => {
        if (previewMode) return;
        e.stopPropagation();
        if (product?.variantType && product?.variantOptions && product.variantOptions.length > 0) {
            setShowVariantDialog(true);
        } else if (product) {
            addToCart(product, undefined);
        }
    };

    const handleAddToCart = () => {
        if (!product?.variantType || selectedVariant) {
            if (product) {
            addToCart(product, selectedVariant);
            }
            setShowVariantDialog(false);
            setSelectedVariant(undefined);
        }
    };

    const handleCloseDialog = () => {
        setShowVariantDialog(false);
        setSelectedVariant(undefined);
    };

    const isOnSale = discount > 0;
    const shoppingCartIconSize = 36;

    const { text: badgeText, textColor, backgroundColor } = getBadgeText({ 
        inStock, 
        isOnSale, 
        discountAmount: discount,
        discountType,
        currency: config.currency
    });

    // Container styles - use Box for preview mode (fixed width), Grid for shop layout
    // Both App.tsx and Portal.tsx set palette.background.paper to cardBackground
    const containerSx = {
        bgcolor: 'background.paper', // Use MUI shorthand for consistency
        flexDirection: 'column',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        borderColor: theme.palette.divider,
        borderRadius: 4,
        padding: 1.5,
        cursor: previewMode ? 'default' : 'pointer',
        ...(width && { width })
    };

    const content = (
        <>
            <Box sx={{ position: 'relative', mb: 2 }}>
                <View style={{ backgroundColor: 'white', overflow: 'hidden', borderRadius: 10 }}>
                    {imageSource ? (
                        <Image
                            source={imageSource}
                            style={{
                                aspectRatio: 1,
                                width: '100%'
                            }}
                            resizeMode={thumbnailResizeMode as any}
                        />
                    ) : (
                        <Box
                            sx={{
                                aspectRatio: '1/1',
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: theme.palette.action.hover,
                                color: theme.palette.text.disabled
                            }}
                        >
                            <Typography variant='caption' sx={{ fontFamily: 'primaryFont' }}>
                                No Image
                            </Typography>
                        </Box>
                    )}
                </View>
                
                {/* Badge */}
                {badgeText && (
                    <Box
                    sx={{
                        position: 'absolute',
                        top: 12,
                        right: -12,
                        backgroundColor: backgroundColor,
                        borderTopLeftRadius: 8,
                        borderBottomLeftRadius: 8,
                        padding: 1.5,
                        paddingY: 0.5,
                            boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <Typography
                        variant='caption'
                        sx={{
                            color: textColor,
                            fontSize: '10px',
                            fontWeight: 'bold',
                        }}
                    >
                            {badgeText}
                    </Typography>
                    </Box>
                )}
                
                {/* Shopping Cart Button */}
                <Tooltip title='Add To Cart' placement='top' arrow>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: -15,
                            right: 10,
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            width: shoppingCartIconSize,
                            height: shoppingCartIconSize,
                            aspectRatio: 1,
                            '&:hover': {
                                backgroundColor: previewMode ? theme.palette.primary.main : theme.palette.primary.dark,
                            },
                            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                        }}
                        onClick={handleCartClick}
                    >
                        <ShoppingCartIcon sx={{ fontSize: shoppingCartIconSize / 2 }} />
                    </IconButton>
                </Tooltip>
            </Box>
            
            {/* Product Info */}
            <Box>
                <Typography
                    variant='subtitle1'
                    sx={{
                        fontSize: 16,
                        fontWeight: 'bold',
                        letterSpacing: 0.5,
                        fontFamily: primaryFontFamily,
                        color: theme.palette.text.primary,
                        marginBottom: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {name}
                </Typography>

                <ProductPrice
                    price={Number(price)}
                    discount={Number(discount)}
                    discountType={discountType}
                />

                {/* Star Rating - Only show if enabled */}
                {showRating && (
                    <Rating
                        name='read-only'
                        value={5}
                        readOnly
                        size='small'
                        sx={{
                            marginTop: 0.5,
                            '& .MuiRating-iconFilled': {
                                color: '#FFC107',
                            },
                        }}
                    />
                )}
            </Box>

            {/* Variant Selection Dialog - Only render in interactive mode */}
            {!previewMode && product && (
            <Dialog
                open={showVariantDialog}
                onClose={handleCloseDialog}
                maxWidth='sm'
                fullWidth
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(50, 50, 50, 0.98)' : 'rgba(255, 255, 255, 0.98)',
                        backdropFilter: 'blur(10px)',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: '2rem',
                        pb: 1,
                        pt: 3,
                        px: 4,
                        color: theme.palette.text.primary,
                    }}
                >
                    Select {product.variantType}
                </DialogTitle>
                <DialogContent sx={{ px: 4, py: 3 }}>
                    <Typography
                        variant='h6'
                        color='text.secondary'
                        sx={{
                            mb: 3,
                            fontSize: '1.25rem',
                            fontWeight: 400,
                        }}
                    >
                        {product.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        {product.variantOptions?.map((option) => (
                            <Button
                                key={option}
                                variant={selectedVariant === option ? 'contained' : 'outlined'}
                                onClick={() => setSelectedVariant(option)}
                                sx={{
                                    minWidth: 100,
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                    fontWeight: 500,
                                    border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)'}`,
                                    borderRadius: 2,
                                    backgroundColor: selectedVariant === option
                                        ? theme.palette.primary.main
                                        : theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'transparent',
                                    color: selectedVariant === option ? 'white' : theme.palette.text.primary,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: selectedVariant === option
                                            ? theme.palette.primary.dark
                                            : theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.1)'
                                                : 'rgba(0, 0, 0, 0.04)',
                                        borderColor: theme.palette.primary.main,
                                        transform: 'translateY(-2px)',
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 4px 12px rgba(0, 0, 0, 0.5)'
                                            : '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    }
                                }}
                            >
                                {option}
                            </Button>
                        ))}
                    </Box>
                    {product.variantType && !selectedVariant && (
                        <Typography
                            variant='body2'
                            sx={{
                                display: 'block',
                                mt: 2,
                                color: '#f44336',
                                fontSize: '0.95rem',
                                fontWeight: 500,
                            }}
                        >
                            Please select a {product.variantType.toLowerCase()}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 4, pb: 3, pt: 2, gap: 2 }}>
                    <Button
                        onClick={handleCloseDialog}
                        sx={{
                            color: theme.palette.text.primary,
                            fontSize: '1rem',
                            fontWeight: 600,
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddToCart}
                        variant='contained'
                        disabled={!selectedVariant}
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            fontSize: '1rem',
                            fontWeight: 600,
                            px: 4,
                            py: 1.25,
                            textTransform: 'none',
                            borderRadius: 2,
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            '&:hover': {
                                backgroundColor: theme.palette.primary.dark,
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                            },
                            '&:disabled': {
                                backgroundColor: theme.palette.action.disabledBackground,
                                color: theme.palette.action.disabled,
                            }
                        }}
                    >
                        Add to Cart
                    </Button>
                </DialogActions>
            </Dialog>
            )}
        </>
    );

    // Use Box for preview mode (fixed width), Grid for shop layout
    if (previewMode || width) {
        return (
            <Box 
                sx={{
                    ...containerSx,
                    pointerEvents: previewMode ? 'none' : 'auto'
                }} 
                onClick={previewMode ? undefined : handleProductPress}
            >
                {content}
            </Box>
        );
    }

    const breakpoints: Record<GridDensity, { xs: number; sm: number; lg: number }> = {
        compact: { xs: 3.5, sm: 2.5, lg: 1.8 },
        standard: { xs: 5.35, sm: 3.5, lg: 2.5 },
        comfortable: { xs: 11, sm: 5.5, lg: 3.5 },
    };
    const bp = breakpoints[gridDensity];

    return (
        <Grid
            item
            xs={bp.xs}
            sm={bp.sm}
            lg={bp.lg}
            sx={containerSx}
            onClick={handleProductPress}
        >
            {content}
        </Grid>
    );
});
